import { Component } from '@angular/core';
import { OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from '../../../services/user.service';
import { ReferralService } from '../../services/referral.service';
import { ReferralManagementComponent } from '../../components/referral-management/referral-management.component';

/**
 * Main workflow referrals management component.
 */
@Component({
	selector: 'workflow-referrals',
	templateUrl: './referrals.component.html',
	styleUrl: './referrals.component.css'
})
export class ReferralsComponent implements OnInit, OnDestroy {

	private referralEventSubscription!: Subscription;
	private userServiceSubscription!: Subscription;

	LOCALIZATION: any;

	inprogress: boolean = false;

	referrals: any[] = [];
	selectedReferrals = {
		all: false,
		selected: 0,
		canCancel: false,
		canComplete: false
	};

	filters = [
		{ name: '', label: '' },
		{ name: 'status', label: 'Status' }
	];
	filter = {
		active: this.filters[0],
		value: ''
	};
	filterOpenState: boolean = true;

	constraints: any = {};
	pagination = {
		page: 1,
		elementsPerPage: 10,
		elementsPerPageSizes: [10, 20, 50],
		sorting: {
			sort: 'dateDue,desc',
		},
		totalElements: 0,
		pageChanged: (newPage: any) => {
			this.pagination.page = newPage;
			this.loadReferrals(this.pagination.page);
		}
	};

	constructor(
		private ngbModal: NgbModal,
		private userService: UserService,
		private referralService: ReferralService
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
	}

	ngOnInit(): void {
		this.referralEventSubscription = this.referralService.workflowReferralEvent.subscribe(event => {
			console.log('Event received:', event);
			this.loadReferrals();
		});
		
		this.userServiceSubscription = this.userService.broadcastEvent.subscribe((eventId: string) => {
			if ("localizationLoaded" === eventId) {
				this.LOCALIZATION = this.userService.LOCALIZATION;
			}
		});			

		this.loadReferrals();
	}

	ngOnDestroy() {
		if (this.referralEventSubscription) {
			this.referralEventSubscription.unsubscribe();
		}
		if (this.userServiceSubscription) {
			this.userServiceSubscription.unsubscribe();
		}		
	}

	/**
	 * Loads the referrals using the current constraints.
	 */
	loadReferrals(page?: number): void {
		this.inprogress = true;

		if (page) {
			this.pagination.page = page;
		}

		let index = this.pagination.page - 1;

		this.referralService.getReferrals(index, this.pagination, this.constraints).then(
			(data: any) => {
				if (!data) {
					this.referrals = [];
				} else {
					this.referrals = data.content;
					this.pagination.page = data.page.number + 1;
					this.pagination.elementsPerPage = data.page.size;
					this.pagination.totalElements = data.page.totalElements;
				}
				this.inprogress = false;
				this.updateReferralStatus();
			},
			(err: any) => {
				console.error("Failed to fetch referrals", err);
				this.referrals = [];
				this.inprogress = false;
				this.updateReferralStatus();
			}
		);
	}

	clearFilter(): void {
		this.constraints = {};
		this.filter.active = this.filters[0];
		this.filter.value = '';
		//this.applyFilter();
		this.loadReferrals();
	}

	applyFilter(event?: Event): void {
		this.constraints = {};
		if (this.filter.active.name) {
			this.constraints[this.filter.active.name] = this.filter.value;
		}

		// FIXME: For some reason this is a separate service call and not simply part of constraints
		// this.loadReferrals();
		this.loadReferralsForStatus();
	}

	private loadReferralsForStatus(page?: number): void {
		this.inprogress = true;

		if (page) {
			this.pagination.page = page;
		}

		let index = this.pagination.page - 1;

		this.referralService.getReferralsByStatus(this.filter.value, index, this.pagination, this.constraints).then(
			(data: any) => {
				if (!data) {
					this.referrals = [];
				} else {
					this.referrals = data.content;
					this.pagination.page = data.page.number + 1;
					this.pagination.elementsPerPage = data.page.size;
					this.pagination.totalElements = data.page.totalElements;
				}
				this.inprogress = false;
				this.updateReferralStatus();
			},
			(err: any) => {
				console.error("Failed to fetch referrals", err);
				this.referrals = [];
				this.inprogress = false;
				this.updateReferralStatus();
			}
		);
	}

	private updateReferralStatus(args?: any): void {
		this.selectedReferrals = {
			all: false,
			selected: 0,
			canCancel: false,
			canComplete: false
		};

		let allSelectedArePending = true;

		this.referrals.forEach((referral: any) => {
			if (args && args.hasOwnProperty('allToggled')) referral.selected = args.allToggled;

			if (referral.selected) {
				this.selectedReferrals.selected = this.selectedReferrals.selected + 1;

				if (referral.status !== 'PENDING') {
					allSelectedArePending = false;
				}
			}
		});

		if (allSelectedArePending && this.selectedReferrals.selected > 0) {
			this.selectedReferrals.canCancel = true;
			this.selectedReferrals.canComplete = true;
		}
	}

	toggleAllReferrals(): void {
		let toggleStatus = !this.selectedReferrals.all;
		this.referrals.forEach((referral: any) => {
			referral.selected = toggleStatus;
		});
		this.updateReferralStatus({ allToggled: toggleStatus });
	}

	toggleReferralId(id: number): void {
		this.selectedReferrals.all = this.referrals.every((r: any) => r.selected);
		this.updateReferralStatus();
	}

	/**
	 * Completes the chosen referrals.
	 */
	onCompleteReferral(id?: number): void {
		const modalRef = this.ngbModal.open(ReferralManagementComponent);

		let ids: number[] = [];
		if (id) {
			ids = [id];
		} else {
			for (let i = 0, len = this.referrals.length; i < len; i++) {
				let r = this.referrals[i];
				if (r.selected) { ids.push(r.id); }
			}
		}

		modalRef.componentInstance.referralGroup = {
			ids: ids,
			mode: 'complete',
			comment: null
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Cancels the chosen referrals.
	 */
	onCancel(id?: number): void {
		const modalRef = this.ngbModal.open(ReferralManagementComponent);

		let ids: number[] = [];
		if (id) {
			ids = [id];
		} else {
			for (let i = 0, len = this.referrals.length; i < len; i++) {
				let r = this.referrals[i];
				if (r.selected) { ids.push(r.id); }
			}
		}

		modalRef.componentInstance.referralGroup = {
			ids: ids,
			mode: 'cancel',
			comment: null
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}
}
